import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { usePWAInstall } from './usePWAInstall';
import { Localize } from './Localize';
import { isPwaExcluded, setManifestLinkEnabled } from '../../shared/pwaUtility';
import history from '../../history';
import './InstallPrompt.scss';

const DEFAULT_LANG = 'en_US';

// Keep in sync with public/manifest.json — every install must show the same
// fixed Blitzz branding regardless of tenant/domain (per task-36383).
const APP_NAME = 'Blitzz Visual Assist';

const InstallPrompt = () => {
  const { canInstall, platform, promptInstall, dismiss } = usePWAInstall();
  const { selectedlanguage } = useSelector((state) => state.language);
  const [t, setT] = useState(Localize[DEFAULT_LANG]);
  const [isExcluded, setIsExcluded] = useState(() =>
    typeof window !== 'undefined' && isPwaExcluded(window.location.pathname, window.location.host)
  );

  useEffect(() => {
    if (selectedlanguage?.id) {
      setT(Localize[selectedlanguage.id] || Localize[DEFAULT_LANG]);
    }
  }, [selectedlanguage]);

  useEffect(() => {
    if (!history) return undefined;
    return history.listen((location) => {
      setIsExcluded(isPwaExcluded(location.pathname, window.location.host));
    });
  }, []);

  useEffect(() => {
    setManifestLinkEnabled(!isExcluded);
  }, [isExcluded]);

  if (isExcluded) return null;
  if (!canInstall) return null;

  const title = (t.pwa_install_title || '').replace('{appName}', APP_NAME);

  if (platform === 'chromium') {
    return (
      <div className="pwa-install-prompt" role="dialog" aria-label={t.install_application || 'Install application'}>
        <div className="pwa-prompt-text">
          <strong>{title}</strong>
          <span>{t.pwa_install_chromium_subtitle}</span>
        </div>
        <div className="pwa-prompt-actions">
          <button type="button" onClick={dismiss} className="pwa-btn-secondary">
            {t.pwa_install_not_now_btn}
          </button>
          <button type="button" onClick={promptInstall} className="pwa-btn-primary">
            {t.pwa_install_install_btn}
          </button>
        </div>
      </div>
    );
  }

  if (platform === 'ios') {
    return (
      <div className="pwa-install-prompt pwa-install-prompt--ios" role="dialog" aria-label={t.install_application || 'Install application'}>
        <div className="pwa-prompt-text">
          <strong>{title}</strong>
          <span>{t.pwa_install_ios_subtitle}</span>
        </div>
        <button type="button" onClick={dismiss} className="pwa-btn-secondary">
          {t.pwa_install_got_it_btn}
        </button>
      </div>
    );
  }

  if (platform === 'mac-safari') {
    return (
      <div className="pwa-install-prompt" role="dialog" aria-label={t.install_application || 'Install application'}>
        <div className="pwa-prompt-text">
          <strong>{title}</strong>
          <span>{t.pwa_install_mac_safari_subtitle}</span>
        </div>
        <button type="button" onClick={dismiss} className="pwa-btn-secondary">
          {t.pwa_install_got_it_btn}
        </button>
      </div>
    );
  }

  return null;
};

export default InstallPrompt;
